import { createFileRoute } from '@tanstack/react-router';
import { useVocWeekly } from '@/hooks/useVoc';
import { TrendChart } from '@/components/charts/TrendChart';
import { ErrorState } from '@/components/feedback/ErrorState';
import type { VocFinding, VocThemeCount, VocWeeklyReport } from '@/lib/api';

/**
 * @graph-node {Page}
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business VoCレポートページ。Channel Talkの問い合わせを月曜〜日曜で週次にまとめ、実測（件数）と分析（生成文）を分けて表示する。実データ未配線のあいだはサンプルである旨を画面に明示する
 * @graph-connects useVocWeekly [calls] VoC週次レポート取得フック
 * @graph-connects TrendChart [calls] 問い合わせ件数の推移を描画
 * @graph-connects ErrorState [calls] エラー表示描画
 */
export const Route = createFileRoute('/voc')({
  component: VocPage,
});

/** 確度の表示。生成文を読む側が重みづけできるようにする */
/** @graph-connects none */
const CONFIDENCE_LABEL: Record<VocFinding['confidence'], string> = {
  high: '確度 高',
  medium: '確度 中',
  low: '確度 低',
};

/** @graph-connects none */
const CONFIDENCE_COLOR: Record<VocFinding['confidence'], string> = {
  high: 'var(--color-success)',
  medium: 'var(--color-primary)',
  low: 'var(--color-text-muted)',
};

/** 前週比を符号付きで表す。比較できない週は null */
/** @graph-connects none */
function formatDelta(current: number, previous: number | null): string | null {
  if (previous === null) return null;
  const delta = current - previous;
  return `${delta >= 0 ? '+' : ''}${delta.toLocaleString()}`;
}

/** 'YYYY-MM-DD' を 'M/D' にする。週の範囲表示で年を2回出さないため */
/** @graph-connects none */
function toShortDate(date: string): string {
  const [, month = '', day = ''] = date.split('-');
  return `${month.replace(/^0/, '')}/${day.replace(/^0/, '')}`;
}

/** @graph-connects none */
function SampleNotice() {
  return (
    <div
      className="glass-card p-4 mb-6"
      style={{ borderLeft: '3px solid rgba(217, 119, 6, 0.9)' }}
      role="note"
      aria-label="サンプル表示の注意"
    >
      <p className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>
        これはサンプルです。実際の問い合わせを分析した結果ではありません。
      </p>
      <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
        Channel Talk の実データ（PIIマスク済みの問い合わせ本文）への接続が未了のため、
        画面の形を確認するための仮のデータを表示しています。件数・所見・推奨アクションの
        いずれも意思決定に使わないでください。配線が済むとこの注意書きは自動的に消えます。
      </p>
    </div>
  );
}

/** @graph-connects none */
function ThemeBar({ theme, max }: { theme: VocThemeCount; max: number }) {
  const delta = formatDelta(theme.count, theme.prevCount);
  return (
    <li className="mb-3">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
          {theme.label}
        </span>
        <span className="text-sm tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
          {theme.count.toLocaleString()} 件
          {delta !== null && (
            <span className="ml-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              前週比 {delta}
            </span>
          )}
        </span>
      </div>
      <div
        className="h-2 rounded-full"
        style={{ background: 'var(--color-surface-subtle, rgba(127,127,127,0.15))' }}
      >
        <div
          className="h-2 rounded-full"
          style={{
            width: `${String(max === 0 ? 0 : Math.round((theme.count / max) * 100))}%`,
            background: 'var(--color-primary)',
          }}
        />
      </div>
    </li>
  );
}

/** @graph-connects none */
function FindingCard({ finding, label }: { finding: VocFinding; label: string }) {
  return (
    <article className="glass-card p-5 mb-4">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          {label}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            担当候補 {finding.owner}
          </span>
          <span
            className="text-xs font-semibold"
            style={{ color: CONFIDENCE_COLOR[finding.confidence] }}
          >
            {CONFIDENCE_LABEL[finding.confidence]}
          </span>
        </div>
      </div>
      <dl className="text-sm leading-relaxed">
        <dt className="text-xs font-semibold mt-3" style={{ color: 'var(--color-text-muted)' }}>
          何が起きているか
        </dt>
        <dd style={{ color: 'var(--color-text-secondary)' }}>{finding.observation}</dd>
        <dt className="text-xs font-semibold mt-3" style={{ color: 'var(--color-text-muted)' }}>
          なぜ起きているか（仮説）
        </dt>
        <dd style={{ color: 'var(--color-text-secondary)' }}>{finding.hypothesis}</dd>
        <dt className="text-xs font-semibold mt-3" style={{ color: 'var(--color-text-muted)' }}>
          事業インパクト
        </dt>
        <dd style={{ color: 'var(--color-text-secondary)' }}>{finding.impact}</dd>
        <dt className="text-xs font-semibold mt-3" style={{ color: 'var(--color-text-muted)' }}>
          推奨アクション
        </dt>
        <dd style={{ color: 'var(--color-text-primary)' }}>{finding.recommendation}</dd>
      </dl>
    </article>
  );
}

/** @graph-connects none */
function ReportBody({ report }: { report: VocWeeklyReport }) {
  const { measured, analysis } = report;
  const maxThemeCount = measured.themes.reduce((max, theme) => Math.max(max, theme.count), 0);
  const themeLabel = new Map(measured.themes.map((theme) => [theme.key, theme.label]));
  const totalDelta = formatDelta(measured.inquiryCount, measured.prevInquiryCount);

  return (
    <>
      {/* エグゼクティブサマリー */}
      <section className="glass-card p-5 mb-6">
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-muted)' }}>
          今週の結論
        </p>
        <p className="text-base leading-relaxed" style={{ color: 'var(--color-text-primary)' }}>
          {analysis.headline}
        </p>
      </section>

      {/* 実測。ここだけが機械が数えた数字 */}
      <section className="mb-6">
        <div className="flex items-baseline gap-2 mb-3 flex-wrap">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            実測
          </h2>
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            問い合わせを数えた結果。以降の所見と違い、生成AIは介在していない
          </span>
        </div>
        <div className="glass-card p-5">
          <div className="flex items-baseline gap-3 mb-4 flex-wrap">
            <span
              className="text-3xl font-semibold tabular-nums"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {measured.inquiryCount.toLocaleString()}
            </span>
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              件
            </span>
            {totalDelta !== null && (
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                前週比 {totalDelta} 件
              </span>
            )}
          </div>
          <ul aria-label="論点別の件数">
            {measured.themes.map((theme) => (
              <ThemeBar key={theme.key} theme={theme} max={maxThemeCount} />
            ))}
          </ul>
        </div>
      </section>

      {/* 分析。生成文であることを見出しで明示する */}
      <section className="mb-6">
        <div className="flex items-baseline gap-2 mb-3 flex-wrap">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            分析
          </h2>
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            生成AIによる所見。上の実測と違い、解釈が入っている
          </span>
        </div>
        {analysis.findings.map((finding) => (
          <FindingCard
            key={finding.themeKey}
            finding={finding}
            label={themeLabel.get(finding.themeKey) ?? finding.themeKey}
          />
        ))}
      </section>

      {analysis.shifts.length > 0 && (
        <section className="glass-card p-5 mb-6">
          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-muted)' }}>
            前週からの構造変化
          </p>
          <ul className="text-sm leading-relaxed">
            {analysis.shifts.map((shift) => (
              <li key={shift} className="mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                ・{shift}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/** @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す */
function VocPage() {
  const { data, isLoading, isError, refetch } = useVocWeekly();
  const report = data?.data.report ?? null;
  const trend = (data?.data.trend ?? []).map((point) => ({
    period: toShortDate(point.weekStart),
    value: point.inquiryCount,
  }));

  return (
    <div style={{ animation: 'fadeIn 0.4s ease' }}>
      <div className="mb-6 flex items-baseline gap-3 flex-wrap">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          VoC レポート
        </h1>
        {report && (
          <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
            対象週: {report.weekStart} 〜 {toShortDate(report.weekEnd)}（月〜日）
          </span>
        )}
      </div>

      {isError && (
        <ErrorState
          title="データ取得エラー"
          message="VoC レポートの取得に失敗しました。"
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      {!isError && isLoading && (
        <div className="glass-card p-5 skeleton-shimmer h-48" aria-label="読み込み中" />
      )}

      {!isError && !isLoading && report === null && (
        <div className="glass-card p-5">
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            確定した週のレポートがまだありません。週が終わると自動で作成されます。
          </p>
        </div>
      )}

      {!isError && !isLoading && report !== null && (
        <>
          {report.source === 'mock' && <SampleNotice />}
          <ReportBody report={report} />
          <section className="glass-card p-5">
            <p className="text-xs font-semibold mb-3" style={{ color: 'var(--color-text-muted)' }}>
              問い合わせ件数の推移（週次・月曜起点）
            </p>
            <TrendChart
              data={trend}
              yLabel="問い合わせ件数"
              xLabel="期間（週次・月曜起点）"
              unit="count"
              color="var(--color-primary)"
              height={200}
            />
          </section>
        </>
      )}
    </div>
  );
}
